import type { QueryClient } from "@tanstack/react-query";

import { todaysItemsQueryKeys } from "@/api/provider/billing/todays-items-query";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import { stockQueryKeys } from "@/query/inventory/stock";
import { medicationsQueryKeys } from "@/query/medications/medications";

/**
 * Inventory changes affect available capacity as well as a dose's owned ledger.
 * Mark reads stale without replaying replica-backed patient reads immediately.
 * Refund mutations do not always know the patient, so invalidate displayed dose
 * records in that case, but leave unrelated patient and medication data alone.
 */
export async function invalidateDoseInventory(
  queryClient: QueryClient,
  patientId?: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: stockQueryKeys.all,
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: ["lot-numbers"],
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: medicationsQueryKeys.all,
      predicate: ({ queryKey }) =>
        queryKey[1] === "available-lots" ||
        queryKey[1] === "available-lots-across-medications" ||
        queryKey[2] === "inventory-preview",
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: patientId
        ? todaysItemsQueryKeys.patient(patientId)
        : todaysItemsQueryKeys.all,
      refetchType: "none",
    }),
    queryClient.invalidateQueries({
      queryKey: patientQueryKeys.all,
      predicate: ({ queryKey }) =>
        (queryKey[2] === "detail" || queryKey[2] === "edit-history") &&
        (!patientId || queryKey[3] === patientId),
      refetchType: "none",
    }),
  ]);
}
